/**
 * Eventos de conversão — Meta Pixel + Google Analytics 4.
 *
 * O Pixel (fbq) e o GA4 (gtag) são carregados no index.html e só disparam
 * `PageView`/`page_view` no carregamento inicial. Estes helpers cobrem o resto
 * do funil. O `Purchase` NÃO fica aqui: sai do backend via Conversions API
 * (webhook do Mercado Pago), que é o único ponto confiável — o PIX não volta
 * pro site. Ver backend/app/services/meta_capi.py.
 */

type FbqParams = Record<string, unknown>;

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

function fbTrack(event: string, params?: FbqParams): void {
  try {
    if (params) window.fbq?.('track', event, params);
    else window.fbq?.('track', event);
  } catch {
    /* pixel indisponível (adblock, etc) — silencioso */
  }
}

function ga(event: string, params?: FbqParams): void {
  try {
    window.gtag?.('event', event, params || {});
  } catch {
    /* gtag indisponível — silencioso */
  }
}

/** Nova rota na SPA. O Pixel já contou a 1ª página no load; chame nas seguintes. */
export function trackPageView(path: string): void {
  fbTrack('PageView');
  ga('page_view', { page_path: path, page_location: window.location.href });
}

/** Cadastro concluído. */
export function trackRegistration(): void {
  fbTrack('CompleteRegistration', { status: true });
  ga('sign_up', { method: 'email' });
}

/** Usuário iniciou o checkout (gerou PIX, abriu checkout de cartão ou assinatura). */
export function trackInitiateCheckout(value: number, opts?: { currency?: string; method?: string; mode?: string }): void {
  const currency = opts?.currency || 'BRL';
  fbTrack('InitiateCheckout', {
    value: Number(value) || 0,
    currency,
    ...(opts?.method ? { payment_method: opts.method } : {}),
    ...(opts?.mode ? { content_category: opts.mode } : {}),
  });
  ga('begin_checkout', {
    value: Number(value) || 0,
    currency,
    ...(opts?.method ? { payment_type: opts.method } : {}),
  });
}

/** Lead de topo de funil — ex.: leitura grátis na página /escolha. */
export function trackLead(source: string): void {
  fbTrack('Lead', { content_name: source });
  // `source` é parâmetro reservado no GA4 (vira origem da sessão) — por isso `lead_source`.
  ga('generate_lead', { lead_source: source });
}

/** Convite de instalação do PWA: 'shown' | 'accepted' | 'dismissed' | 'ios_help'. */
export function trackInstallPrompt(action: string, platform?: string): void {
  const params = { content_name: 'pwa_install', action, ...(platform ? { platform } : {}) };
  fbTrack('CustomizeProduct', params);
  ga('pwa_install_prompt', { action, ...(platform ? { platform } : {}) });
}
